"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const TextField_1 = __importDefault(require("@material-ui/core/TextField"));
const Autocomplete_1 = __importDefault(require("@material-ui/lab/Autocomplete"));
const Link_1 = __importDefault(require("@material-ui/core/Link"));
const Search_1 = __importDefault(require("@material-ui/icons/Search"));
const core_1 = require("@material-ui/core");
const uuid_1 = require("uuid");
const LoadingIndicator_1 = __importDefault(require("../LoadingIndicator"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const Icon_1 = __importDefault(require("../Icon"));
const getFlattenOptions_1 = require("../../utils/getFlattenOptions");
const useStyles = core_1.makeStyles((theme) => ({
    input: {
        display: 'inline-block',
        marginRight: '20px',
    },
    recoveryLink: {
        pointerEvents: 'auto',
    },
    textfield: {
        paddingRight: '30px',
    },
}));
/**
 * An autosuggest control is a normal text input enhanced by a panel of suggested options.
 * */
function Autosuggest({ options = [], value, filteringType = 'auto', loadingText = 'Loading ...', errorText = 'Error fetching', recoveryText, name, controlId = uuid_1.v4(), empty, disabled, placeholder, invalid, ariaRequired = false, freeSolo = false, disableClearable = false, icon = undefined, ariaDescribedby, ariaLabelledby, onChange, onInputChange, onFocus, onBlur, onRecoveryClick, disableBrowserAutocorrect = false, statusType = 'finished', }) {
    const classes = useStyles();
    const [inputValue, setInputValue] = react_1.useState(value || null);
    const [open, setOpen] = react_1.default.useState(false);
    const autoCompleteString = disableBrowserAutocorrect ? 'off' : 'on';
    const onRecoveryClickHandler = react_1.useCallback((event) => {
        event.preventDefault();
        onRecoveryClick === null || onRecoveryClick === void 0 ? void 0 : onRecoveryClick(event);
        event.stopPropagation();
    }, [onRecoveryClick]);
    const flattenOptions = react_1.useMemo(() => {
        return getFlattenOptions_1.getFlattenOptions(options);
    }, [options]);
    const handleOnChange = react_1.useCallback((_, value) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
        setInputValue(value);
    }, [onChange, setInputValue]);
    const handleOnInput = react_1.useCallback((event, value, reason) => {
        if (filteringType === 'manual') {
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(event, value, reason);
        }
    }, [onInputChange, filteringType]);
    const getOptionsSelected = react_1.useCallback((option, value) => {
        return option.value === value.value;
    }, []);
    const loadingAndErrorText = react_1.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null,
        statusType === 'loading' && react_1.default.createElement(LoadingIndicator_1.default, { label: loadingText }),
        statusType === 'error' && (react_1.default.createElement(StatusIndicator_1.default, { statusType: "negative" },
            errorText,
            ".",
            recoveryText && (react_1.default.createElement("span", null,
                react_1.default.createElement(Link_1.default, { href: "#", onClick: onRecoveryClickHandler, className: classes.recoveryLink }, recoveryText))))))), [statusType, classes.recoveryLink, errorText, recoveryText, loadingText, onRecoveryClickHandler]);
    const textfield = react_1.useCallback((params) => (react_1.default.createElement(TextField_1.default, Object.assign({ autoCorrect: autoCompleteString, placeholder: placeholder, required: ariaRequired, error: !!(statusType === 'error' || invalid) }, params, { variant: "outlined", size: "small", margin: "normal", name: name, InputProps: Object.assign(Object.assign({ 'aria-required': ariaRequired, 'aria-describedby': ariaDescribedby, 'aria-labelledby': ariaLabelledby }, params.InputProps), { className: classes.textfield, type: 'search', startAdornment: (react_1.default.createElement(core_1.InputAdornment, { position: "start" },
                icon === undefined && react_1.default.createElement(Search_1.default, { color: "action" }),
                icon && react_1.default.createElement(Icon_1.default, { name: icon, color: "action" }))) }) }))), [
        ariaDescribedby,
        ariaLabelledby,
        ariaRequired,
        autoCompleteString,
        classes.textfield,
        icon,
        invalid,
        name,
        placeholder,
        statusType,
    ]);
    return (react_1.default.createElement("div", null,
        react_1.default.createElement(Autocomplete_1.default, { "data-testid": "autosuggest", disabled: disabled, autoHighlight: true, popupIcon: null, freeSolo: freeSolo, disableClearable: disableClearable, open: statusType === 'error' || statusType === 'loading' ? true : open, id: controlId, value: inputValue, noOptionsText: empty, options: flattenOptions, groupBy: (option) => option.group || '', loadingText: loadingAndErrorText, getOptionSelected: getOptionsSelected, onChange: handleOnChange, onInputChange: handleOnInput, onOpen: (e) => {
                setOpen(true);
                onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
            }, onClose: (e) => {
                setOpen(false);
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
            }, loading: statusType !== 'finished', getOptionLabel: (option) => option.label || '', renderInput: textfield })));
}
exports.default = Autosuggest;
//# sourceMappingURL=index.js.map